import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../home-landing/components/Footer';
import Icon from '../../components/AppIcon';
import CookiePreferences from './components/CookiePreferences';
import CookieScanner from './components/CookieScanner';

const CookiePolicy = () => {
  const lastUpdated = 'January 28, 2026';

  const cookieCategories = [
    {
      id: 'essential',
      name: 'Essential Cookies',
      icon: 'Shield',
      description: 'These cookies are necessary for the website to function and cannot be disabled. They are usually set in response to actions you take, such as setting privacy preferences, logging in, or filling in forms.',
      examples: [
        { name: 'session_id', purpose: 'Maintains user session state', expiry: 'Session' },
        { name: 'auth_token', purpose: 'Authentication and security', expiry: '7 days' },
        { name: 'cookie_consent', purpose: 'Stores cookie preferences', expiry: '1 year' }
      ],
      required: true
    },
    {
      id: 'functional',
      name: 'Functional Cookies',
      icon: 'Settings',
      description: 'These cookies enable enhanced functionality and personalization, such as remembering your preferences, language settings, and customized content.',
      examples: [
        { name: 'theme_preference', purpose: 'Remembers dark/light mode choice', expiry: '1 year' },
        { name: 'language', purpose: 'Stores language preference', expiry: '1 year' },
        { name: 'layout_settings', purpose: 'Saves UI customization', expiry: '6 months' }
      ],
      required: false
    },
    {
      id: 'analytics',
      name: 'Analytics Cookies',
      icon: 'BarChart',
      description: 'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. This helps us improve our website performance and user experience.',
      examples: [
        { name: '_ga', purpose: 'Google Analytics - tracks user behavior', expiry: '2 years' },
        { name: '_gid', purpose: 'Google Analytics - distinguishes users', expiry: '24 hours' },
        { name: 'analytics_session', purpose: 'Internal analytics tracking', expiry: '30 minutes' }
      ],
      thirdParty: [
        { name: 'Google Analytics', policy: 'https://policies.google.com/privacy' }
      ],
      required: false
    },
    {
      id: 'marketing',
      name: 'Marketing Cookies',
      icon: 'Target',
      description: 'These cookies track your online activity to help advertisers deliver more relevant advertising or limit how many times you see an ad. They may be set by us or third-party providers.',
      examples: [
        { name: 'ad_id', purpose: 'Tracks ad campaign performance', expiry: '90 days' },
        { name: 'conversion_tracking', purpose: 'Measures marketing effectiveness', expiry: '30 days' }
      ],
      thirdParty: [
        { name: 'Google Ads', policy: 'https://policies.google.com/technologies/ads' },
        { name: 'Facebook Pixel', policy: 'https://www.facebook.com/privacy/explanation' }
      ],
      required: false
    }
  ];

  return (
    <>
      <Helmet>
        <title>Cookie Policy - Portfolio</title>
        <meta name="description" content="Learn about how we use cookies and manage your cookie preferences. GDPR-compliant cookie policy with detailed information about cookie types and purposes." />
      </Helmet>
      <Header />
      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-accent/20 rounded-lg flex items-center justify-center">
                  <Icon name="Cookie" size={24} color="var(--color-accent)" />
                </div>
                <h1 className="text-4xl md:text-5xl font-bold">Cookie Policy</h1>
              </div>
              <p className="text-lg text-gray-300 mb-4">
                Transparent information about how we use cookies to enhance your browsing experience
              </p>
              <p className="text-sm text-gray-400">
                Last Updated: <span className="text-accent">{lastUpdated}</span>
              </p>
            </div>
          </div>
        </div>

        {/* Quick Summary */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
            <div className="flex gap-3">
              <Icon name="Info" size={24} color="var(--color-accent)" className="flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-lg font-semibold text-foreground mb-2">Quick Summary</h2>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  We use cookies to ensure our website functions properly, remember your preferences, analyze site traffic, and deliver relevant content. 
                  You have full control over non-essential cookies through our preference center below. Essential cookies cannot be disabled as they are 
                  necessary for the website to work.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Cookie Preference Controls */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8">
          <CookiePreferences categories={cookieCategories} />
        </div>

        {/* Detailed Cookie Information */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8">
          <h2 className="text-3xl font-bold text-foreground mb-8">Cookie Categories</h2>
          <div className="space-y-6">
            {cookieCategories?.map((category) => (
              <div key={category?.id} className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name={category?.icon} size={24} color="var(--color-accent)" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-semibold text-foreground">{category?.name}</h3>
                      {category?.required && (
                        <span className="px-2 py-1 bg-accent/20 text-accent text-xs font-medium rounded">
                          Required
                        </span>
                      )}
                    </div>
                    <p className="text-muted-foreground mb-4">{category?.description}</p>
                  </div>
                </div>

                {/* Examples Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-2 px-3 text-muted-foreground font-medium">Cookie Name</th>
                        <th className="text-left py-2 px-3 text-muted-foreground font-medium">Purpose</th>
                        <th className="text-left py-2 px-3 text-muted-foreground font-medium">Expiry</th>
                      </tr>
                    </thead>
                    <tbody>
                      {category?.examples?.map((cookie, idx) => (
                        <tr key={idx} className="border-b border-border last:border-0">
                          <td className="py-3 px-3 font-mono text-xs text-foreground">{cookie?.name}</td>
                          <td className="py-3 px-3 text-muted-foreground">{cookie?.purpose}</td>
                          <td className="py-3 px-3 text-muted-foreground">{cookie?.expiry}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Third Party Services */}
                {category?.thirdParty && category?.thirdParty?.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <h4 className="text-sm font-semibold text-foreground mb-2">Third-Party Services:</h4>
                    <ul className="space-y-2">
                      {category?.thirdParty?.map((service, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm">
                          <Icon name="ExternalLink" size={14} color="var(--color-muted-foreground)" />
                          <span className="text-muted-foreground">{service?.name}</span>
                          <a 
                            href={service?.policy} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-accent hover:underline"
                          >
                            Privacy Policy
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Cookie Scanner */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8">
          <CookieScanner />
        </div>

        {/* Browser Management Instructions */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8">
          <div className="bg-card border border-border rounded-lg p-6">
            <h2 className="text-2xl font-bold text-foreground mb-4">Managing Cookies in Your Browser</h2>
            <p className="text-muted-foreground mb-4">
              You can control and manage cookies through your browser settings. Please note that removing or blocking cookies may impact your user experience and some features may no longer function properly.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <h3 className="font-semibold text-foreground">Popular Browsers:</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <Icon name="Chrome" size={16} color="var(--color-muted-foreground)" />
                    <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
                      Google Chrome
                    </a>
                  </li>
                  <li className="flex items-center gap-2">
                    <Icon name="Globe" size={16} color="var(--color-muted-foreground)" />
                    <a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
                      Mozilla Firefox
                    </a>
                  </li>
                  <li className="flex items-center gap-2">
                    <Icon name="Globe" size={16} color="var(--color-muted-foreground)" />
                    <a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
                      Safari
                    </a>
                  </li>
                  <li className="flex items-center gap-2">
                    <Icon name="Globe" size={16} color="var(--color-muted-foreground)" />
                    <a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
                      Microsoft Edge
                    </a>
                  </li>
                </ul>
              </div>
              <div className="space-y-3">
                <h3 className="font-semibold text-foreground">Opt-Out Tools:</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <Icon name="Shield" size={16} color="var(--color-muted-foreground)" />
                    <a href="https://optout.networkadvertising.org/" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
                      NAI Opt-Out Tool
                    </a>
                  </li>
                  <li className="flex items-center gap-2">
                    <Icon name="Shield" size={16} color="var(--color-muted-foreground)" />
                    <a href="https://optout.aboutads.info/" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
                      DAA Opt-Out Tool
                    </a>
                  </li>
                  <li className="flex items-center gap-2">
                    <Icon name="Shield" size={16} color="var(--color-muted-foreground)" />
                    <a href="https://www.youronlinechoices.com/" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
                      Your Online Choices (EU)
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8 pb-16">
          <div className="bg-gradient-to-r from-accent/10 to-accent/5 border border-accent/20 rounded-lg p-6">
            <h2 className="text-xl font-bold text-foreground mb-2">Questions About Cookies?</h2>
            <p className="text-muted-foreground mb-4">
              If you have any questions about our use of cookies or this Cookie Policy, please contact us at:
            </p>
            <a href="mailto:privacy@example.com" className="text-accent hover:underline font-medium">
              privacy@example.com
            </a>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default CookiePolicy;